import { useEffect, useState } from "react";
import { Mail, Copy, Trash2, Send, RefreshCw, Check, AlertTriangle, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  listShares, createShare, updateShare, revokeShare, resendShare,
} from "@/lib/api";

const ROLE_LABEL = { view: "View only", edit: "Can edit" };

function inviteUrl(token) {
  return `${window.location.origin}/invite/${token}`;
}

function StatusPill({ status, email_status }) {
  if (status === "accepted") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-emerald-500/10 text-[#059669] border border-emerald-500/20">
        <Check className="w-3 h-3" /> Accepted
      </span>
    );
  }
  if (email_status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 border border-sage-200">
        <Send className="w-3 h-3" /> Email sent · pending
      </span>
    );
  }
  if (email_status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30">
        <AlertTriangle className="w-3 h-3" /> Email failed · share link manually
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-black/[0.06] text-[#57534E] border border-black/[0.08]">
      <Mail className="w-3 h-3" /> Pending · share link manually
    </span>
  );
}

export default function ShareDialog({ open, onOpenChange, project }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("view");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const rows = await listShares(project.id);
      setShares(rows);
    } catch {
      toast.error("Could not load shares");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("view");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const row = await createShare(project.id, { email: trimmed, role });
      setShares((prev) => {
        const without = prev.filter((s) => s.id !== row.id && s.email !== row.email);
        return [row, ...without];
      });
      setEmail("");
      if (row.email_status === "sent") {
        toast.success(`Invite emailed to ${row.email}`);
      } else if (row.email_status === "failed") {
        toast.warning("Email could not be sent — copy the invite link below");
      } else {
        toast.success("Share created — copy the invite link to send manually");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not invite this email";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (s, newRole) => {
    if (s.role === newRole) return;
    try {
      const updated = await updateShare(project.id, s.id, { role: newRole });
      setShares((prev) => prev.map((row) => (row.id === s.id ? updated : row)));
      toast.success(`Updated to ${ROLE_LABEL[newRole]}`);
    } catch {
      toast.error("Could not update role");
    }
  };

  const handleRevoke = async (s) => {
    if (!window.confirm(`Revoke access for ${s.email}?`)) return;
    try {
      await revokeShare(project.id, s.id);
      setShares((prev) => prev.filter((row) => row.id !== s.id));
      toast.success(`Revoked ${s.email}`);
    } catch {
      toast.error("Could not revoke");
    }
  };

  const handleResend = async (s) => {
    try {
      const updated = await resendShare(project.id, s.id);
      setShares((prev) => prev.map((row) => (row.id === s.id ? updated : row)));
      if (updated.email_status === "sent") toast.success(`Re-sent invite to ${s.email}`);
      else toast.warning("Email could not be sent — copy the invite link below");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not resend";
      toast.error(detail);
    }
  };

  const handleCopy = async (s) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(s.token));
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C]">Share this project</DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">
            Invite collaborators by email. <strong>View only</strong> = can read but not edit. <strong>Can edit</strong> = full read + write (cannot delete the project). Invites expire in 7 days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-stretch py-2">
          <Input
            data-testid="share-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="fc-input h-10 flex-1"
            autoFocus
          />
          <select
            data-testid="share-role-select"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="fc-input h-10 px-2 text-sm rounded-md sm:w-[140px]"
          >
            <option value="view">View only</option>
            <option value="edit">Can edit</option>
          </select>
          <Button
            type="submit"
            data-testid="share-invite-submit"
            disabled={submitting}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-10 px-4"
          >
            {submitting ? "Sending…" : "Invite"}
          </Button>
        </form>

        <div className="mt-2">
          <div className="fc-label mb-2">People with access</div>
          {loading && <div className="text-sm text-[#A8A29E]">Loading…</div>}
          {!loading && shares.length === 0 && (
            <div
              data-testid="share-empty"
              className="rounded-lg border border-dashed border-black/[0.1] bg-sage-50/40 p-4 text-sm text-[#57534E]"
            >
              Only you have access. Invite someone above to collaborate.
            </div>
          )}
          {!loading && shares.length > 0 && (
            <ul data-testid="share-list" className="space-y-2">
              {shares.map((s) => (
                <li
                  key={s.id}
                  data-testid={`share-row-${s.id}`}
                  className="rounded-lg border border-black/[0.06] bg-white p-3 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1C1C1C] truncate">{s.email}</div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <StatusPill status={s.status} email_status={s.email_status} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <select
                        data-testid={`share-row-role-${s.id}`}
                        value={s.role}
                        onChange={(e) => handleRoleChange(s, e.target.value)}
                        className="fc-input h-8 px-2 text-xs rounded-md"
                        title="Permission"
                      >
                        <option value="view">View only</option>
                        <option value="edit">Can edit</option>
                      </select>
                      <button
                        type="button"
                        data-testid={`share-row-revoke-${s.id}`}
                        onClick={() => handleRevoke(s)}
                        title="Revoke access"
                        className="h-8 w-8 grid place-items-center rounded-md border border-black/[0.08] hover:border-[#DC2626] text-[#A8A29E] hover:text-[#DC2626] transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {s.status !== "accepted" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <code
                        data-testid={`share-row-link-${s.id}`}
                        className="text-[11px] text-[#57534E] bg-black/[0.04] px-2 py-1 rounded truncate flex-1 min-w-0"
                      >
                        {inviteUrl(s.token)}
                      </code>
                      <button
                        type="button"
                        data-testid={`share-row-copy-${s.id}`}
                        onClick={() => handleCopy(s)}
                        className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-black/[0.08] hover:border-black/[0.25] text-[11px] text-[#27272A] bg-white hover:bg-[#F5F5F4] transition-colors"
                      >
                        <Copy className="w-3 h-3" /> Copy link
                      </button>
                      <button
                        type="button"
                        data-testid={`share-row-resend-${s.id}`}
                        onClick={() => handleResend(s)}
                        title="Generate a fresh link and re-send the email"
                        className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-black/[0.08] hover:border-black/[0.25] text-[11px] text-[#27272A] bg-white hover:bg-[#F5F5F4] transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" /> Resend
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            data-testid="share-dialog-close"
            onClick={() => onOpenChange(false)}
            className="text-sm text-[#78716C] hover:text-[#1C1C1C] inline-flex items-center gap-1.5"
          >
            <X className="w-3.5 h-3.5" /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
